import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Source} from "react-map-gl/mapbox";
import {useOperator} from "../../../context/OperatorContext";
import Spinner from "../../Spinner";
import Map, {VisitorLocationsLayer} from "../../Map";
import {useQuery} from "react-query";
import ngage from "../../../services/ngage";
import {DateTime} from "luxon";
import {useInView} from "react-intersection-observer";
import Icon from "../../Icon";
import IconName from "../../../types/IconName";
import {useAuth} from "../../../context/AuthContext";

// Constants ----

const FETCH_INTERVAL = 3000;

// Types ----

type VisitorsLocationMapProps = {
  height: string
  siteId?: number | null
  showSiteSelector?: boolean
}

// Component ----

export function VisitorsLocationMap({height, siteId, showSiteSelector}: VisitorsLocationMapProps) {
  const {settings} = useOperator();
  const {can} = useAuth();

  const { ref, inView } = useInView({ threshold: 0.3 });

  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(siteId ?? null);

  // Fetch site data
  const {data: site, isLoading: siteLoading} = useQuery(
    ['site', {id: selectedSiteId}],
    () => ngage.sites.get(Number(selectedSiteId)),
    {enabled: can('sites:read') && !!selectedSiteId}
  )

  const since = useMemo(() => site ? DateTime.now()?.setZone(site.timezone).startOf('day') : undefined, [site]);
  const until = useMemo(() => site ? DateTime.now()?.setZone(site.timezone) : undefined, [site]);

  // Fetch visitors location data
  const {data: visitorLocationData, isLoading: mapDataLoading} = useQuery(
    ['maps.visitorLocation', {since, until, siteId: selectedSiteId}],
    ({signal}) => ngage.maps.visitorLocation({since, until, siteId: selectedSiteId}, {signal}),
    {
      enabled: inView && !!selectedSiteId && !!since && !!until,
      keepPreviousData: true,
      staleTime: 0,
      refetchInterval: FETCH_INTERVAL,
      refetchIntervalInBackground: false,
    },
  );

  // Update the selected site ID when the siteId prop changes
  useEffect(() => {
    if (!siteId) return;

    setSelectedSiteId(siteId);
  }, [siteId]);

  const handleSiteSelect = useCallback((siteId: number | null) => setSelectedSiteId(siteId), [])

  return (
    <div ref={ref}>
      <Map
        height={height}
        defaultMapStyle={settings?.audience_app.map_style ?? null}
        initialViewState={settings?.audience_app.map_state ?? null}
        hidePointsOfInterest
        onSelectSite={handleSiteSelect}
        showSiteSelector={showSiteSelector}
        siteId={selectedSiteId}
      >
        {/* Visitor count */}
        <div className='flex flex-col gap-2 absolute items-end left-[8px] top-[8px]'>
          <div className={`flex gap-1 bg-white rounded-md border border-gray-300 px-3 py-2 text-sm`}>
            <Icon name={IconName.USER} className='w-4 h-4 text-gray-400 flex-shrink-0' />
            <p>
              {visitorLocationData?.features.length.toLocaleString() ?? 0} {`${visitorLocationData?.features.length === 1 ? 'app user' : 'app users'}`}
            </p>
          </div>
        </div>

        {/* Data Sources */}
        {!!visitorLocationData &&
          <Source id="visitors-location-source" type="geojson" data={visitorLocationData}>
            <VisitorLocationsLayer id="visitors-location" />
          </Source>
        }

        {/* Loading Spinner */}
        {(mapDataLoading || siteLoading) &&
          <div className="absolute top-0 left-0 h-full w-full flex items-center justify-center pointer-events-none">
            <Spinner color={Spinner.colors.DARK} size={Spinner.sizes.MEDIUM}/>
          </div>
        }
      </Map>
    </div>
  );
}